<template>
  <modal
    name="support-ticket-create"
    :width="820"
    height="auto"
    :adaptive="true"
    :scrollable="true"
    class="support-create-modal modal-not-overflow"
    @before-open="prepare"
  >
    <section class="support-create" aria-labelledby="support-create-title">
      <app-modal-header
        design="new"
        eyebrow="Support"
        title="Create support ticket"
        title-id="support-create-title"
        @close="close"
      />
      <form class="support-create__body" @submit.prevent="submit">
        <app-card design="new" padding="compact" title="What happened?" subtitle="A clear subject and one concrete example help Support start faster.">
          <app-form-group v-if="isInternal" design="new" label="Requester" required>
            <app-select
              v-model="form.requester"
              design="new"
              :options="users"
              label="full_name"
              placeholder="Search an active Board user"
              @search="searchUsers"
            />
          </app-form-group>
          <app-form-group design="new" label="Subject" required>
            <app-input v-model="form.subject" design="new" placeholder="Short summary of the problem" />
          </app-form-group>
          <app-form-group design="new" label="Description" required>
            <app-textarea v-model="form.description" design="new" placeholder="What did you try, what happened, and what did you expect?" @paste.native="pasteFiles" />
          </app-form-group>
          <app-form-group design="new" label="System" required>
            <div class="support-create__system-switch" role="radiogroup" aria-label="System">
              <button
                v-for="system in orderedSystems"
                :key="system.id"
                type="button"
                class="support-create__system-option"
                :class="{ 'support-create__system-option--active': form.system && form.system.id === system.id }"
                :style="{ '--system-color': system.color || '#2563EB' }"
                role="radio"
                :aria-checked="form.system && form.system.id === system.id ? 'true' : 'false'"
                @click="form.system = system"
              >
                <i aria-hidden="true"></i>
                <span>{{ system.name }}</span>
              </button>
            </div>
          </app-form-group>
          <div class="support-create__attachments">
            <label class="support-create__drop">
              <input type="file" multiple :accept="acceptedFiles" @change="selectFiles">
              <strong>Attach screenshots or documents</strong>
              <span>PNG, JPG, PDF, DOC, DOCX, XLS, XLSX or CSV · max 10 files · 20 MB each</span>
            </label>
            <support-file-preview-list v-if="files.length" queued :files="files" @remove="files.splice($event.index, 1)" />
          </div>
        </app-card>

        <app-card v-if="isInternal" design="new" padding="compact" title="Internal routing" subtitle="Optional. The ticket still enters New and stays unassigned unless you choose an owner.">
          <div class="support-create__grid">
            <app-form-group design="new" label="Assignee">
              <app-select v-model="form.assignee" design="new" :options="assignees" label="full_name" placeholder="Unassigned" />
            </app-form-group>
            <dictionary-field
              v-model="form.direction"
              kind="directions"
              label="Direction"
              singular="Direction"
              :options="directions"
              :can-create="Boolean(form.system)"
              :parent-payload="{ system: form.system && form.system.id }"
              @created="directions.push($event)"
            />
            <dictionary-field
              v-model="form.module"
              kind="modules"
              label="Module"
              singular="Module"
              :options="modules"
              :can-create="Boolean(form.direction)"
              :parent-payload="{ direction: form.direction && form.direction.id }"
              @created="modules.push($event)"
            />
            <dictionary-field
              v-model="form.tags"
              kind="tags"
              label="Tags"
              singular="Tag"
              :options="tags"
              multiple
              can-create
              @created="tags.push($event)"
            />
            <dictionary-field
              v-model="form.responsibleLabels"
              kind="responsible_labels"
              label="Responsible labels"
              singular="Responsible label"
              :options="responsibleLabels"
              multiple
              can-create
              @created="responsibleLabels.push($event)"
            />
          </div>
        </app-card>

        <p v-if="error" class="support-create__error" role="alert">{{ error }}</p>
        <footer class="support-create__footer">
          <span v-if="contextLabel" class="support-create__context">Context: {{ contextLabel }}</span>
          <div>
            <app-button design="new" theme="dark" type="button" @click.native="close">Cancel</app-button>
            <app-button design="new" theme="success" type="submit" :disabled="working || !canSubmit">
              {{ working ? 'Creating…' : 'Create ticket' }}
            </app-button>
          </div>
        </footer>
      </form>
    </section>
  </modal>
</template>

<script>
import { techSupportKey } from '@/utils/techSupportAccess'
import {
  getSupportTicketDictionary,
  getSupportTicketUsers,
  postSupportTicket
} from '@/http'
import DictionaryField from './DictionaryField'
import SupportFilePreviewList from './SupportFilePreviewList'
import { apiError, idempotencyKey, normalizePage } from '../ticketState'
import { appendSupportFiles, clipboardSupportFiles, uploadSupportFiles } from '../supportFileUploads'

const emptyForm = () => ({
  requester: null,
  subject: '',
  description: '',
  system: null,
  direction: null,
  module: null,
  assignee: null,
  tags: [],
  responsibleLabels: []
})

export default {
  name: 'SupportTicketCreateModal',
  components: { DictionaryField, SupportFilePreviewList },
  data() {
    return {
      form: emptyForm(),
      pageContext: null,
      files: [],
      systems: [],
      directions: [],
      modules: [],
      tags: [],
      responsibleLabels: [],
      users: [],
      assignees: [],
      working: false,
      error: '',
      acceptedFiles: '.jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.csv'
    }
  },
  computed: {
    isInternal() {
      return this.$store.getters['permissions/can'](techSupportKey('support', 'edit'))
    },
    canSubmit() {
      return Boolean(
        this.form.subject?.trim()
        && this.form.description?.trim()
        && this.form.system
        && (!this.isInternal || this.form.requester)
      )
    },
    orderedSystems() {
      const order = { Board: 0, Story: 1, Other: 2 }
      return [...this.systems].sort((left, right) => {
        const leftOrder = order[left.name] ?? 99
        const rightOrder = order[right.name] ?? 99
        return leftOrder - rightOrder || left.name.localeCompare(right.name)
      })
    },
    contextLabel() {
      return this.pageContext?.page_title || this.$route.meta?.title || this.$route.name || ''
    }
  },
  watch: {
    'form.system'(system) {
      if (this.form.direction?.system !== system?.id) {
        this.form.direction = null
        this.form.module = null
      }
      this.loadDirections()
    },
    'form.direction'(direction) {
      if (this.form.module?.direction !== direction?.id) this.form.module = null
      this.loadModules()
    }
  },
  methods: {
    prepare(event) {
      this.form = emptyForm()
      this.form.subject = event?.params?.subject || ''
      if (this.isInternal) this.form.requester = event?.params?.requester || null
      this.pageContext = {
        route_name: this.$route.name,
        path: this.$route.path,
        page_title: this.$route.meta?.title || document.title
      }
      this.files = []
      this.error = ''
      const requests = [getSupportTicketDictionary('systems', { active: true })]
      if (this.isInternal) {
        requests.push(getSupportTicketDictionary('tags', { active: true }))
        requests.push(getSupportTicketDictionary('responsible_labels', { active: true }))
        requests.push(getSupportTicketUsers({ page_size: 50 }))
        requests.push(getSupportTicketUsers({ page_size: 50, kind: 'assignee' }))
      }
      Promise.all(requests).then(responses => {
        this.systems = normalizePage(responses[0].data)
        this.form.system = this.systems.find(item => item.name === 'Board') || this.systems[0] || null
        if (this.isInternal) {
          this.tags = normalizePage(responses[1].data)
          this.responsibleLabels = normalizePage(responses[2].data)
          this.users = normalizePage(responses[3].data)
          if (this.form.requester && !this.users.some(user => user.id === this.form.requester.id)) this.users.unshift(this.form.requester)
          this.assignees = normalizePage(responses[4].data)
        }
      }).catch(error => { this.error = apiError(error, 'Unable to prepare the ticket form.') })
    },
    close() {
      this.$modal.hide('support-ticket-create')
    },
    searchUsers(search, loading) {
      if (!this.isInternal || !search || search.length < 2) return
      loading(true)
      getSupportTicketUsers({ search, page_size: 30 }).then(response => {
        this.users = normalizePage(response.data)
      }).finally(() => loading(false))
    },
    loadDirections() {
      if (!this.isInternal || !this.form.system) {
        this.directions = []
        return
      }
      getSupportTicketDictionary('directions', { active: true, system: this.form.system.id })
        .then(response => { this.directions = normalizePage(response.data) })
    },
    loadModules() {
      if (!this.isInternal || !this.form.direction) {
        this.modules = []
        return
      }
      getSupportTicketDictionary('modules', { active: true, direction: this.form.direction.id })
        .then(response => { this.modules = normalizePage(response.data) })
    },
    selectFiles(input) {
      const incoming = Array.isArray(input) ? input : Array.from(input.target.files || [])
      const result = appendSupportFiles(this.files, incoming)
      this.files = result.files
      this.error = result.error
      if (input.target) input.target.value = ''
    },
    pasteFiles(event) {
      const files = clipboardSupportFiles(event)
      if (files.length) this.selectFiles(files)
    },
    async submit() {
      if (!this.canSubmit || this.working) return
      const data = {
        subject: this.form.subject.trim(),
        description: this.form.description.trim(),
        system: this.form.system.id,
        page_context: this.pageContext
      }
      if (this.isInternal) {
        data.requester = this.form.requester.id
        if (this.form.assignee) data.assignee = this.form.assignee.id
        if (this.form.direction) data.direction = this.form.direction.id
        if (this.form.module) data.module = this.form.module.id
        data.tags = this.form.tags.map(value => value.id)
        data.responsible_labels = this.form.responsibleLabels.map(value => value.id)
      }
      this.working = true
      this.error = ''
      let uploaded = []
      try {
        uploaded = await uploadSupportFiles(this.files)
        data.attachments = uploaded.map(file => file.id)
        const response = await postSupportTicket(data, idempotencyKey())
        this.$emit('created', response.data)
        this.$root.$emit('support-ticket-created', response.data)
        this.$notify({ type: 'success', text: `${response.data.public_number} created.` })
        this.close()
      } catch (error) {
        this.error = apiError(error)
      } finally {
        this.working = false
      }
    }
  }
}
</script>

<style lang="scss">
.support-create-modal > .vm--modal {
  overflow: hidden!important;
  border: 1px solid $ui-line!important;
  border-radius: 18px!important;
  box-shadow: 0 22px 70px rgba(28, 45, 74, .2)!important;
}
</style>

<style lang="scss" scoped>
.support-create {
  background: $ui-page;
  color: $ui-text;
  font-family: $ui-font-body;
}

.support-create__body {
  display: grid;
  gap: $ui-space-16;
  max-height: calc(92vh - 82px);
  padding: $ui-space-20;
  overflow-y: auto;
}

.support-create__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 $ui-space-16;
}

.support-create__system-switch {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: $ui-space-8;
}

.support-create__system-option {
  display: inline-flex;
  min-width: 96px;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  gap: $ui-space-8;
  padding: 0 $ui-space-12;
  border: 1px solid $ui-line-strong;
  border-radius: 9px;
  background: $ui-surface;
  color: $ui-text;
  cursor: pointer;
  font: inherit;
  font-size: $ui-font-size-control;
  font-weight: 600;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease, box-shadow .15s ease, transform .15s ease;
}

.support-create__system-option:hover {
  border-color: var(--system-color);
  background: $ui-surface-soft;
  transform: translateY(-1px);
}

.support-create__system-option i {
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border-radius: 50%;
  background: var(--system-color);
  box-shadow: 0 0 0 3px rgba(100, 116, 139, .1);
}

.support-create__system-option:focus-visible {
  outline: 2px solid $ui-blue;
  outline-offset: 2px;
}

.support-create__system-option--active {
  border-color: var(--system-color);
  background: $ui-blue-soft;
  color: $ui-ink;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, .08), 0 3px 9px rgba(15, 23, 42, .07);
}

.support-create__drop {
  display: flex;
  min-height: 88px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: $ui-space-4;
  padding: $ui-space-16;
  border: 1px dashed $ui-line-strong;
  border-radius: $ui-panel-radius;
  background: $ui-surface-soft;
  color: $ui-muted;
  cursor: pointer;
  text-align: center;
}

.support-create__drop input { position: absolute; width: 1px; height: 1px; overflow: hidden; }
.support-create__drop strong { color: $ui-blue; font-size: $ui-font-size-body; }
.support-create__drop span { font-size: $ui-font-size-metadata; }
.support-create__files { display: grid; gap: $ui-space-8; margin-top: $ui-space-12; }
.support-create__files li { display: flex; justify-content: space-between; gap: $ui-space-8; padding: $ui-space-8 $ui-space-12; border-radius: $ui-control-radius; background: $ui-surface-soft; }
.support-create__files button { border: 0; background: transparent; color: $ui-red; cursor: pointer; font-size: 18px; }
.support-create__error { margin: 0; padding: $ui-space-12; border-radius: $ui-control-radius; background: rgba($ui-red, .08); color: $ui-red; }
.support-create__footer { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-16; padding-top: $ui-space-4; }
.support-create__footer > div { display: flex; gap: $ui-space-12; }
.support-create__context { color: $ui-muted; font-size: $ui-font-size-metadata; }

@media (max-width: 700px) {
  .support-create__body { padding: $ui-space-16; }
  .support-create__grid { grid-template-columns: 1fr; }
  .support-create__footer { align-items: stretch; flex-direction: column; }
  .support-create__footer > div { justify-content: flex-end; }
  .support-create__system-option { min-width: 0; flex: 1 1 calc(50% - #{$ui-space-4}); }
}
</style>
